import { Drawer as AntDrawer } from 'antd';
import type { DrawerProps as AntDrawerProps } from 'antd';
import type React from 'react';

import { withAuthGuard } from '../../auth';
import type { AuthProps } from '../../auth';

export type DrawerProps = AntDrawerProps & AuthProps;

// EAA-1812: antd's own default width (378px) reads cramped once a panel carries
// labelled form fields — the shape every admin "view/edit X" side panel in the
// apps takes. 480px is the width those hand-rolled drawers converged on before
// this shared wrapper existed, so it becomes the new baseline default here
// (still overridable per call site via the `width` prop).
const DEFAULT_WIDTH = 480;

function DrawerWithDefaults({
    placement = 'right',
    width = DEFAULT_WIDTH,
    ...rest
}: AntDrawerProps): React.ReactElement {
    return <AntDrawer placement={placement} width={width} {...rest} />;
}

const GuardedDrawer = withAuthGuard(DrawerWithDefaults, {
    defaultFallback: 'hide',
}) as unknown as React.ForwardRefExoticComponent<DrawerProps & React.RefAttributes<HTMLDivElement>>;

/**
 * Tier-1 presentational primitive: themed, auth-gated pass-through of antd's
 * `Drawer`, defaulting to `placement="right"` and a 480px `width` tuned for
 * admin detail panels rather than antd's cramped 378px default. Everything
 * else (props, slots, imperative `open` control) is antd's own — this does
 * not reimplement focus trapping or portalling. `authFallback="hide"` by
 * default, matching `Modal`: an ungranted drawer renders nothing rather than
 * a disabled shell.
 */
export const Drawer = GuardedDrawer;

export default Drawer;
